import React from 'react';

import {useSelector} from 'react-redux';

import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';
import {DashboardPage} from 'app/components/general/DashboardPage';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {DashboardSidebarTagsList} from 'app/components/general/DashboardPage/DashboardSidebarTagsList';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {DocumentBulkActionsButton} from 'app/components/general/DocumentBulkActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {FolderLink} from 'app/folders/components/FolderLink';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {usePolicies} from 'app/policies/api';
import {usePolicyFilters} from 'app/policies/hooks';
import {toggleDashboardRelation} from 'app/policies/slice';
import {POLICIES_DASHBOARD_URL, POLICIES_NEW_POLICY} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {CompactPolicyListItem} from './CompactPolicyListItem';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  PoliciesFetchParamsType,
  PoliciesLocationParamsType,
} from 'app/policies/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, DetailPolicySerializedType} from 'sweet-types';

export const PoliciesDashboard = (
  routerProps: WithRouterProps<unknown, PoliciesLocationParamsType>
) => {
  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);
  const currentUser = useCurrentUserState().data!;

  const actions = useAsyncActions({toggleDashboardRelation});
  const activeRelations = useSelector((state) => state.policies.relations);

  return (
    <DashboardPage<
      DetailPolicySerializedType,
      PoliciesLocationParamsType,
      PoliciesFetchParamsType
    >
      {...routerProps}
      actionArea={
        <CreateDocumentButton contentType="policy" disabled={!isTeamMember} />
      }
      sidebar={
        <>
          <DashboardSidebarFoldersList />
          <DashboardSidebarTagsList />
        </>
      }
      header={({resetFilters, params, count}) =>
        params.folder != null ? (
          <div className="my-2 text-center">
            <h4>
              <i className="far fa-folder-open" /> {gt('Folder contents')}
              <span>
                <FolderLink hashid={params.folder} className="small ml-3" />
              </span>
            </h4>
            {count != 0 ? (
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all policies.')}
              </button>
            ) : null}
          </div>
        ) : null
      }
      useDataHook={usePolicies}
      useFiltersHook={usePolicyFilters}
      contentType={'policy'}
      enableNotifications={true}
      enableBulkSelect={currentUser.is_manager}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="policy"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          show={{
            team: true,
            tags: true,
            sort: true,
            filters: true,
            search: true,
          }}
        />
      )}
      renderItem={(
        policy,
        {params, refetch},
        isSelected,
        addToSelection,
        removeFromSelection,
        notifications
      ) => (
        <CompactPolicyListItem
          key={policy.id}
          object={policy}
          refreshData={refetch}
          sortBy={params.ordering}
          notifications={notifications}
          activeRelations={activeRelations}
          toggleRelation={(contentType: ContentType, isOpen: boolean) =>
            actions.toggleDashboardRelation({
              contentType,
              isOpen,
            })
          }
          enableBulkSelect={currentUser.is_manager}
          isSelected={isSelected}
          addToSelection={addToSelection}
          removeFromSelection={removeFromSelection}
        />
      )}
      messages={{
        counter: (count) => ngt('%s policy', '%s policies', count),
        topListActions: (selectedDocsOnThisPage, {refetch}) => (
          <DocumentBulkActionsButton
            documents={selectedDocsOnThisPage.map((selectedDoc) => ({
              document_content_type: selectedDoc.content_type,
              document: selectedDoc,
              url: selectedDoc.url,
              can_edit: selectedDoc.can_edit,
              can_delete: selectedDoc.can_delete,
              object_id: selectedDoc.id,
            }))}
            refreshData={refetch}
            enableBulkSelect={currentUser.is_manager}
          />
        ),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-umbrella"
              head={gt('No policies on your account')}
            >
              {isTeamMember ? (
                <FullHTMLLink
                  className="btn btn-primary"
                  html_url={POLICIES_NEW_POLICY}
                >
                  {gt('Create a new policy')}
                </FullHTMLLink>
              ) : null}
            </NoResultsMessage>
          ) : (
            <NoResultsMessage
              icon="fa-umbrella"
              head={gt('No policies found with these filters')}
            >
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all policies')}
              </button>
            </NoResultsMessage>
          ),
      }}
    />
  );
};

const policyCt = getContentType('policy');

export const PoliciesDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={policyCt.icon}
    text={gt('Policies')}
    link={POLICIES_DASHBOARD_URL}
  />
);
